import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Heart, CheckCircle, X, Archive, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPrayers() {
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState(null);

  const fetchPrayers = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      let url = `${API}/admin/prayer-requests?limit=100`;
      if (filter !== "all") url += `&status=${filter}`;

      const response = await axios.get(url, { headers });
      setPrayers(response.data.prayer_requests);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load prayer requests");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, filter]);

  useEffect(() => {
    fetchPrayers();
  }, [fetchPrayers]);

  const updatePrayerStatus = async (prayerId, newStatus) => {
    const token = localStorage.getItem("admin_token");
    setUpdating(prayerId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/prayer-requests/${prayerId}`, { status: newStatus }, { headers });
      toast.success(`Marked as ${newStatus}`);
      fetchPrayers();
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setUpdating(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
      archived: "bg-zinc-100 text-zinc-600 border-zinc-200"
    };
    return styles[status] || styles.pending;
  };

  return (
    <AdminLayout currentPage="/admin/prayers">
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-prayers">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-[#18181B]">Prayer Requests</h1>
          <p className="text-sm text-[#71717A] mt-1">Review and moderate prayer submissions</p>
        </div>

        {/* Filter */}
        <div className="bg-white border border-[#E4E4E7] p-4 mb-4">
          <div className="flex items-center gap-3">
            <Label className="text-xs font-medium text-[#71717A] uppercase tracking-wide whitespace-nowrap">
              Filter
            </Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full sm:w-40 h-9 border-[#E4E4E7]" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Prayer List */}
        <div className="space-y-3">
          {loading ? (
            <div className="bg-white border border-[#E4E4E7] p-8 text-center text-[#71717A] text-sm">
              Loading...
            </div>
          ) : prayers.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] p-8 text-center text-[#71717A] text-sm">
              No prayer requests found
            </div>
          ) : (
            prayers.map((prayer) => (
              <div 
                key={prayer.id} 
                className="bg-white border border-[#E4E4E7] p-4 sm:p-5"
                data-testid={`prayer-${prayer.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-[#FEF3C7] rounded-full flex items-center justify-center flex-shrink-0">
                      <Heart className="w-4 h-4 text-[#D97706]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#18181B] truncate">
                        {prayer.name || "Anonymous"}
                      </p>
                      {prayer.email_optional && (
                        <p className="text-xs text-[#71717A] truncate">{prayer.email_optional}</p>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border flex-shrink-0 ${getStatusBadge(prayer.status)}`}>
                    {prayer.status}
                  </span>
                </div>

                {/* Request Text */}
                <p className="text-sm text-[#52525B] mb-4 leading-relaxed">
                  {prayer.request_text}
                </p>

                {/* Footer */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-1 text-xs text-[#A1A1AA]">
                    <Clock className="w-3 h-3" />
                    {new Date(prayer.created_at).toLocaleString()}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updatePrayerStatus(prayer.id, 'approved')}
                      disabled={prayer.status === 'approved' || updating === prayer.id}
                      className="h-8 px-3 text-xs border-[#E4E4E7] hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200"
                      data-testid={`approve-${prayer.id}`}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updatePrayerStatus(prayer.id, 'rejected')}
                      disabled={prayer.status === 'rejected' || updating === prayer.id}
                      className="h-8 px-3 text-xs border-[#E4E4E7] hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                      data-testid={`reject-${prayer.id}`}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Reject
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updatePrayerStatus(prayer.id, 'archived')}
                      disabled={prayer.status === 'archived' || updating === prayer.id}
                      className="h-8 px-3 text-xs border-[#E4E4E7] hover:bg-zinc-100"
                      data-testid={`archive-${prayer.id}`}
                    >
                      <Archive className="w-3 h-3 mr-1" />
                      Archive
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
